import React, { useState, useEffect } from 'react';
import { FaUsers, FaStore, FaMoneyBillWave, FaChartLine } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import GlobalStatsCard from '../components/analytics/GlobalStatsCard';
import UserGrowthChart from '../components/analytics/UserGrowthChart';
import RevenueChart from '../components/analytics/RevenueChart';
import adminService from '../services/admin.service';
import { useAuth } from '../context/AuthContext';

function DashboardPage() {
  const navigate = useNavigate();
  const { getAdminToken } = useAuth();
  const [stats, setStats] = useState(null);
  const [userGrowth, setUserGrowth] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [pendingApplications, setPendingApplications] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Global stats
      const statsData = await adminService.getGlobalStats();
      setStats(statsData);
      // Note: stats will be overridden with real revenue/fraud/inactive counts below

      // User growth
      const growthData = await adminService.getUserGrowthData(30);
      setUserGrowth(growthData);

      // Real revenue data from revenue_logs
      const { data: revRes } = await supabase.rpc('admin_get_revenue_logs_full', { p_admin_token: getAdminToken(), p_status: 'paid' });
      const revData = revRes?.success ? revRes.rows : [];
      const totalRevenue = revData?.reduce((s, r) => s + (r.amount || 0), 0) || 0;

      // Build chart data grouped by business name
      const revMap = {};
      revData?.forEach(r => {
        const name = r.business_name || 'Unknown';
        revMap[name] = (revMap[name] || 0) + (r.amount || 0);
      });
      const chartData = Object.entries(revMap)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([name, revenue]) => ({ name, revenue }));
      setRevenueData(chartData.length ? chartData : [{ name: 'Məlumat yoxdur', revenue: 0 }]);

      // Fraud alert count: unresolved fraud_logs
      const { data: fraudData } = await supabase.rpc('admin_count_unresolved_fraud', { p_admin_token: getAdminToken() });
      const fraudCount = fraudData?.success ? fraudData.count : 0;

      // Inactive businesses count
      const { data: inactiveCount } = await supabase.rpc('admin_count_businesses', {
        p_admin_token: getAdminToken(), p_is_active: false,
      });

      // Merge real data into stats
      setStats(prev => ({ ...(prev || statsData || {}), totalRevenue, fraudAlerts: fraudCount || 0, inactiveBusinesses: inactiveCount || 0 }));

      // ⭐ YENİ - Pending applications count
      const { data: appRes } = await supabase.rpc('admin_get_applications', { p_admin_token: getAdminToken(), p_status: 'pending' });
      setPendingApplications(appRes?.success ? appRes.rows.length : 0);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner}></div>
        <p>Yüklənir...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Dashboard</h1>
        <p style={styles.subtitle}>Sistemin ümumi görünüşü</p>
      </div>

      {/* Stats Cards */}
      <div style={styles.statsGrid}>
        <GlobalStatsCard
          icon={FaUsers}
          title="Ümumi İstifadəçilər"
          value={stats?.totalUsers || 0}
          subtitle={`Bu ay: +${stats?.newUsersThisMonth || 0}`}
          color="#FF6B35"
          trend={12.5}
        />

        <GlobalStatsCard
          icon={FaStore}
          title="Aktiv Businesses"
          value={`${stats?.activeBusinesses || 0}/${stats?.totalBusinesses || 0}`}
          subtitle="Aktiv/Ümumi"
          color="#004E89"
        />

        <GlobalStatsCard
          icon={FaMoneyBillWave}
          title="Ümumi Gəlir (Bu ay)"
          value={`${stats?.totalRevenue?.toFixed(2) || 0} ₼`}
          subtitle="Subscription + Commission"
          color="#06D6A0"
          trend={8.3}
        />

        <GlobalStatsCard
          icon={FaChartLine}
          title="Artım Dərəcəsi"
          value="+15.2%"
          subtitle="Son aya nisbətən"
          color="#FFD166"
          trend={15.2}
        />
      </div>

      {/* Charts */}
      <div style={styles.chartsGrid}>
        <div style={styles.chartContainer}>
          <UserGrowthChart data={userGrowth} />
        </div>

        <div style={styles.chartContainer}>
          <RevenueChart data={revenueData} />
        </div>
      </div>

      {/* Quick Actions */}
      <div style={styles.quickActions}>
        {/* ⭐ YENİ - Applications Card */}
        <div style={styles.actionCard}>
          <h3 style={styles.actionTitle}>📝 Yeni Müraciətlər</h3>
          <p style={styles.actionValue}>
            {pendingApplications > 0 ? pendingApplications : 'Yoxdur'}
          </p>
          <button onClick={() => navigate('/applications')} style={styles.actionBtn}>
            {pendingApplications > 0 ? 'Təsdiq Et' : 'Bax'}
          </button>
        </div>

        <div style={styles.actionCard}>
          <h3 style={styles.actionTitle}>🚨 Son Fraud Alerts</h3>
          <p style={styles.actionValue}>{stats?.fraudAlerts ?? 0} yeni alert</p>
          <button onClick={() => navigate('/fraud')} style={styles.actionBtn}>
            Ətraflı Bax
          </button>
        </div>

        <div style={styles.actionCard}>
          <h3 style={styles.actionTitle}>🏢 Pending Subscriptions</h3>
          <p style={styles.actionValue}>{stats?.pendingCount ?? 0} business</p>
          <button onClick={() => navigate('/finance')} style={styles.actionBtn}>
            İdarə Et
          </button>
        </div>

        <div style={styles.actionCard}>
          <h3 style={styles.actionTitle}>⚠️ Inactive Businesses</h3>
          <p style={styles.actionValue}>{stats?.inactiveBusinesses ?? 0} business</p>
          <button onClick={() => navigate('/businesses')} style={styles.actionBtn}>
            Yoxla
          </button>
        </div>

        <div style={styles.actionCard}>
          <h3 style={styles.actionTitle}>🏆 TOP 5 Leaderboards</h3>
          <p style={styles.actionValue}>İstifadəçilər və Business-lər</p>
          <button onClick={() => navigate('/leaderboards')} style={styles.actionBtn}>
            Bax
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '30px',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '5px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#999',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '30px',
  },
  chartsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
    gap: '20px',
    marginBottom: '30px',
  },
  chartContainer: {
    minHeight: '400px',
  },
  quickActions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
  },
  actionCard: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '25px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    textAlign: 'center',
  },
  actionTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '10px',
  },
  actionValue: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#FF6B35',
    marginBottom: '15px',
  },
  actionBtn: {
    padding: '10px 20px',
    backgroundColor: '#F8F9FA',
    color: '#1A1A2E',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '60vh',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '5px solid #f3f3f3',
    borderTop: '5px solid #FF6B35',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
};

export default DashboardPage;